import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react'
import useGlobalStyles from 'styles/globalStyles'
import { useParams } from 'react-router-dom';
import SectionSkeleton from 'components/skeletons/SectionSkeleton';
import PageHeader from 'components/common/PageHeader/PageHeader';
import RegistryPatientInformationDetails from './RegistryPatientInformationDetails';
import RegistryICDDetails from './RegistryICDDetails';
import RegistryProcedureDetailsSection from './RegistryProcedureDetailsSection';
import RegistryCPTDetails from '../RegistryCPTDetails/RegistryCPTDetails';
import CustomFieldsDetails from './RegistryCustomFieldsDetails/RegistryCustomFieldsDetails';
import { RegistryProvider, useRegistryContext } from 'context/RegistryContext';
import { CustomFieldType, ImageItem } from 'types/types/Types';
import { registryProcedureFields } from 'common/constants';
import RegistryPatientNotesDetails from 'components/registry/RegistryPatientNotesDetails/RegistryPatientNotesDetails';
import RegistryPatientOptions from 'components/registry/RegistryPatientOptions/RegistryPatientOptions';
import { RegistryPatientProvider } from 'context/RegistryPatientContext';
import RegistryPatientImagesDetails from 'components/registry/RegistryPatientImagesDetails/RegistryPatientImagesDetails';
import { useGetRegistryPatientById } from 'hooks/useRegistryPatient';

export default function RegistryPatientDetailsPage() {
  const { registryId, patientId } = useParams();

  return (
    <RegistryProvider registryId={Number(registryId)}>
      <RegistryPatientProvider patientId={Number(patientId)}>
        <RegistryPatientDetailsPageWrapper patientId={patientId} />
      </RegistryPatientProvider>
    </RegistryProvider>
  )
}

function RegistryPatientDetailsPageWrapper({ patientId }: any) {
  const { registryId } = useParams();
  const globalStyles = useGlobalStyles();
  const [procedureData, setProcedureData] = useState<any>(null);
  const { registry } = useRegistryContext();
  const [images, setImages] = useState<ImageItem[]>([]);

  const hasCustomFields = useMemo(() => {
    if (!registry || registry.fields == null) return false;
    return registry.fields.some((field: CustomFieldType) => !registryProcedureFields.includes(field.name));
  }, [registry])

  const hasNotes = useMemo(() => {
    if (!registry || registry.fields == null) return false;
    return registry.fields.some((field: CustomFieldType) => field.name == 'notes')
  }, [registry])

  const hasImages = useMemo(() => {
    if (!registry || registry.fields == null) return false;
    return registry.fields.some((field: CustomFieldType) => field.name == 'images')
  }, [registry])

  const { refetch, isFetching } = useGetRegistryPatientById({
    registryId: Number(registryId),
    patientId: Number(patientId),
  }, {
    onSuccess: (data: any) => {

      const newData = { ...data.data };
      setProcedureData(newData);

      setImages(prev => {
        const serverImages: ImageItem[] = newData.data.images ?? [];

        const serverNames = new Set(
          serverImages.map(img => (img.image_name ?? "").trim()).filter(Boolean)
        );

        const localImages = prev.filter(img => {
          if (img.id) return false;
          const name = (img.image_name ?? "").trim();
          return !(name && serverNames.has(name));
        });

        return [...serverImages, ...localImages];
      });

    }
  });


  if (!procedureData || !registry) {
    return (
      <Box className={globalStyles.container} p={3}>
        <Box >
          <PageHeader
            title={'Patient Profile'}
            showPath
            buttons={[]}
          />
        </Box>
        <Box mt={3}>
          <SectionSkeleton count={9} />
        </Box>

        <Box mt={3}>
          <SectionSkeleton count={4} />
        </Box>

        <Box mt={3}>
          <SectionSkeleton count={7} />
        </Box>

        <Box mt={3} />
      </Box>
    )
  }

  return (
    <Box className={globalStyles.container} p={3}>

      <Box>
        <PageHeader
          title={'Patient Profile'}
          showPath
          buttons={[
            <RegistryPatientOptions
              key={'registry-patient-options'}
              patientId={procedureData.id}
            />
          ]}
        />
      </Box>

      <Box mt={3}>
        <RegistryPatientInformationDetails data={procedureData} />
      </Box>

      {procedureData?.data?.diagnosisCodes?.length > 0 && <Box mt={3}>
        <RegistryICDDetails icds={procedureData?.data?.diagnosisCodes || []} />
      </Box>}

      <Box mt={3}>
        <RegistryProcedureDetailsSection data={procedureData} />
      </Box>

      {procedureData?.data?.cptCodes?.length > 0 && <Box mt={3}>
        <RegistryCPTDetails cpts={procedureData?.data?.cptCodes} />
      </Box>}

      {hasCustomFields && <Box mt={3}>
        <CustomFieldsDetails data={procedureData} />
      </Box>}

      {hasNotes && <Box mt={3}>
        <RegistryPatientNotesDetails
          notes={procedureData.data.notes || []}
          data={procedureData}
          procedureId={procedureData.id}
          refetch={refetch}
          isEditor={true} />
      </Box>}

      {hasImages && <Box mt={3}>
        <RegistryPatientImagesDetails
          images={images}
          setImages={setImages}
          procedureId={procedureData.id}
          refetch={refetch}
          isFetching={isFetching}
          isEditor={true}
        />
      </Box>}

      <Box mt={3} />

    </Box>
  )
}
